import React, { useState, useEffect } from 'react';
import { Button, Checkbox, Input } from '../../components/common';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useForm } from 'react-hook-form';
import { FcGoogle } from 'react-icons/fc';
import { routes } from '../../lib/routes';

interface LoginFormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

const LoginForm = () => {
  const { login, googleLogin, error } = useAuth();

  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    watch,
    handleSubmit,
    setError,
    reset,
    formState: { errors },
  } = useForm<LoginFormData>();

  // P2 FIX: Reset form on mount to clear any retained credentials
  useEffect(() => {
    reset({ email: '', password: '', rememberMe: false });
  }, [reset]);

  const disabledSubmit = watch('email') === '' || watch('password') === '';

  const onSubmit = async (data: LoginFormData) => {
    if (isLoading) return; // Prevent double submission
    setIsLoading(true);
    try {
      await login(data);
    } catch (error) {
      console.error('Login failed:', error);
      setError('email', { message: '' });
      setError('password', { message: '' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form className='w-full' autoComplete='off' onSubmit={handleSubmit(onSubmit)}>
      {error && (
        <div className='rounded-md bg-red-50 p-4 mb-3'>
          <div className='text-sm text-red-700'>{error}</div>
        </div>
      )}

      <div className='mb-6'>
        <Input
          label='Email ID'
          type='email'
          autoComplete='new-email'
          placeholder='Enter email ID'
          required
          helperText={errors.email?.message as string}
          color={errors.email ? 'error' : 'default'}
          {...register('email', {
            required: 'Email is required',
            pattern: {
              value: /\S+@\S+\.\S+/,
              message: 'Please enter a valid email',
            },
          })}
        />
      </div>
      <div className='mb-3'>
        <Input
          label='Password'
          type='password'
          autoComplete='new-password'
          placeholder='Enter password'
          required
          helperText={errors.password?.message as string}
          color={errors.password ? 'error' : 'default'}
          {...register('password', {
            required: 'Password is required',
            minLength: {
              value: 6,
              message: 'Password must be at least 6 characters',
            },
          })}
        />
      </div>

      <div className='flex items-center justify-between mb-6'>
        <div className='flex items-center'>
          <Checkbox
            id='remember-me'
            label='Remember me'
            {...register('rememberMe')}
          />
        </div>
        <div>
          <Link
            to={routes.forgotPassword}
            className='font-medium transition-colors'
          >
            Forgot password?
          </Link>
        </div>
      </div>
      <Button
        type='submit'
        color='primary'
        isLoading={isLoading}
        disabled={disabledSubmit}
        className='group relative w-full !bg-primary-600 hover:!bg-primary-700 disabled:!bg-gray-300'
      >
        Login
      </Button>

      <div className='my-4 px-10'>
        <div className='relative'>
          <div className='absolute inset-0 flex items-center'>
            <div className='w-full border-t border-gray-300' />
          </div>
          <div className='relative flex justify-center text-sm'>
            <span className='px-2 bg-white text-gray-500 text-xs'>OR</span>
          </div>
        </div>
      </div>

      <Button
        type='button'
        variant='outline'
        className='w-full'
        onClick={() => googleLogin()}
      >
        <FcGoogle size={24} /> Continue with Google
      </Button>

      <p className='text-gray-400 mb-0 font-semibold text-xs text-center w-[70%] mx-auto mt-6'>
        By signing up to create an account you are accepting our{' '}
        <a href='https://2connect.ai/terms' target='_blank' rel='noopener noreferrer' className='text-primary-500 hover:underline'>
          Terms of Use
        </a>{' '}
        &{' '}
        <a href='https://2connect.ai/privacy' target='_blank' rel='noopener noreferrer' className='text-primary-500 hover:underline'>
          Privacy Policy
        </a>.
      </p>
    </form>
  );
};

export default LoginForm;
